// ---------------------------------------------------------------------------
// DnD Email Editor – Inherit-first color field
//
// A color control whose default state is INHERIT: an empty value ('') means
// the block takes its color from the Design (global) styles. The user only
// stores an explicit value when they deliberately override.
//
//   variant="foreground" → inherit = the global text color
//   variant="background" → inherit = transparent (no background)
//
// The row is compact: a swatch + hex field. Clicking the swatch opens the shared
// popover, where the inherit choice sits alongside the picker and presets.
// ---------------------------------------------------------------------------

import { ColorTrigger, type InheritOption } from './dnd-color-presets';

export interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  variant?: 'foreground' | 'background';
  /** The design-level color this field falls back to; shown as the inherit preview. */
  inheritedColor?: string;
}

// Checker background marks a transparent (no-background) swatch.
const CHECKER_CLASS = 'dnd-swatch-checker';

export function ColorField({
  label,
  value,
  onChange,
  variant = 'foreground',
  inheritedColor,
}: ColorFieldProps) {
  const isCustom = !!value;
  const isBackground = variant === 'background';

  const inherit: InheritOption = {
    label: isBackground ? 'None — transparent' : 'Global color',
    title: isBackground
      ? 'No background — transparent, inherits the design'
      : 'Use the global text color from Design settings',
    active: !isCustom,
    onSelect: () => onChange(''),
  };

  // Swatch preview: the custom color; else the inherited color (foreground) or a
  // transparent checker (background).
  const swatchStyle = isCustom
    ? { backgroundColor: value }
    : isBackground
      ? undefined
      : { backgroundColor: inheritedColor || '#333333' };
  const swatchClass = !isCustom && isBackground ? CHECKER_CLASS : undefined;

  return (
    <div className="dnd-inspector-field">
      <label>{label}</label>
      <ColorTrigger
        value={value}
        onChange={onChange}
        swatchStyle={swatchStyle}
        swatchClass={swatchClass}
        placeholder={isBackground ? 'None' : 'Global'}
        inherit={inherit}
      />
    </div>
  );
}
